import { useEffect, useState } from "react";

import {
  AD_TYPE_OPTIONS,
  TABLE_COLUMNS_OPTIONS,
} from "../../constants/filters";
import { COPY } from "../../copy";
import { monthLabelFromIndex } from "../../customersData";
import { getCohortAdditionalOrders } from "../../monthDetailData";
import {
  applySegmentToOrderMonthRows,
  applySegmentToActivityRows,
  formatOverviewCount,
  formatOverviewCurrency,
  getCurrentMonthIndex,
  getRowBudgetShare,
  type P8ActivityRow,
} from "../../p8OverviewData";
import { renderCopy } from "../../renderCopy";
import type {
  AdsOrganizeBy,
  InsightsCustomerSegment,
  PrototypeVersion,
} from "../../types";
import { useAdTypesModal } from "../AdTypesExplainer";
import {
  useCardPaddingClass,
  useIsCompact,
  useTableRowHeightClass,
} from "../CardDensity";
import { forecastTooltip } from "../ForecastExplainer";
import { InsightsFilterMenu } from "../InsightsFilterMenu";
import { TablePagination } from "../TablePagination";

import { DottedLabel } from "./DottedLabel";
import { HeaderTooltip } from "./HeaderTooltip";
import { ProjectedTag } from "./ProjectedTag";
import { TableSettingsMenu } from "./TableSettingsMenu";

type AdPerformanceByMonthTableProps = {
  version: PrototypeVersion;
  organizeBy: AdsOrganizeBy;
  /** Every month, uncut: the Ad type filter below the title belongs to this table. */
  rows: P8ActivityRow[];
  /**
   * Click-month rows supplying V2's optional cohort section. Its leading
   * columns stay on order month while this section retains the same values as
   * V1's ad-click view and the cohort detail reached from the row.
   */
  cohortRows?: P8ActivityRow[];
  onOpenMonth?: (monthIndex: number) => void;
};

/** Stands in for a figure that belongs in this column but isn't in yet. */
function EmptyCell() {
  return <span className="text-[#757575]">—</span>;
}

/**
 * A year and a half of months per page: long enough to cover a full year plus
 * the run-up to it for a year-over-year read, short enough that the card doesn't
 * bury the footnote below it.
 */
const PAGE_SIZE = 18;

const MONTH_LABEL_CLASS = "text-sm leading-5 font-medium";

/**
 * Month is frozen so the row stays identifiable while the reader scrolls the
 * thirteen columns to its right — a figure in the last column is meaningless if
 * you've lost which month it belongs to.
 *
 * The frozen cells need their own opaque fill. A row's background sits behind
 * every cell in it rather than on top of the ones scrolling past, so without a
 * fill of its own the sticky cell would have other columns' text sliding through
 * it. That's also why the hover fill is repeated here instead of being left to
 * the row, and why the row's bottom border is repeated on the cell: the table
 * collapses borders, which lets that fill paint over the collapsed line and
 * break the divider under this one column. Restating it collapses to the same
 * hairline, so it costs nothing where the browser was already drawing it.
 */
const STICKY_MONTH_CLASS = "sticky left-0 z-10";

/**
 * Only once there's something behind it. The table has no vertical rules, so a
 * permanent divider on this column would read as an arbitrary line rather than
 * as an edge — the shadow appears on scroll and says what it means.
 */
const STICKY_SCROLLED_CLASS = "shadow-[6px_0_6px_-4px_rgba(0,0,0,0.08)]";

/**
 * The two halves of the table: what a month's ads returned straight away, and
 * what that month is on track to return over the following year.
 *
 * First-year sales (actual) sits on the first-year side. It is the part of
 * the first-year figure that has landed so far, so grouping it with the
 * immediate columns would split the first-year total from the half of it
 * that has already arrived.
 *
 * Distinguished by fill rather than by the band alone: two labels over one
 * continuous gray read as one strip with two captions, and a reader scanning
 * the columns needs to know which side of the line a column is on. The
 * first-year band uses the same lavender as the lift tags.
 */
/**
 * Left to wrap, unlike the column headers. Hiding a column narrows the band it
 * belonged to, and a nowrap label in a narrowed band sets a floor on the table's
 * width — so switching a column off would widen the table into a horizontal
 * scroll instead of shrinking it.
 */
const GROUP_HEADER_CLASS =
  "faire-label-medium border-b border-[#dfe0e1] bg-fs-surface-primary px-3 py-2 !text-left";
const GROUP_IMMEDIATE_CLASS = GROUP_HEADER_CLASS;
const GROUP_FIRST_YEAR_CLASS = GROUP_HEADER_CLASS;

const GROUP_COLORS = {
  spend: "#F26532",
  immediate: "#2d2a70",
  continued: "#AFC1FF",
} as const;

function GroupLabel({ color, children }: { color: string; children: string }) {
  return (
    <span className="inline-flex items-center gap-2 text-[#333]">
      <span
        className="size-2 shrink-0 rounded-[2px]"
        style={{ backgroundColor: color }}
        aria-hidden
      />
      {children}
    </span>
  );
}

/**
 * Carried down the first column of each labelled section so the boundary the
 * group header draws doesn't stop at the header. The only vertical rules in the
 * table, which is what makes them read as section edges rather than as
 * arbitrary lines.
 */
const SECTION_EDGE_CLASS = "border-l border-[#dfe0e1]";

/**
 * The Month / Budget split has to live on Month's right edge. Budget already
 * draws `SECTION_EDGE_CLASS` on the left, but Month is sticky and paints over
 * that 1px, so the spend group would otherwise sit flush against the labels.
 */
const MONTH_SECTION_EDGE_CLASS = "border-r border-[#dfe0e1]";

/**
 * Pinned column widths, in render order and in pixels.
 *
 * Auto layout sizes each column to whatever happens to be in it, so changing the
 * customer filter reflows the whole table: figures become em dashes, digit
 * counts change, and every column shifts. Holding the widths still is the point
 * — the shape of the table shouldn't be what tells you the filter moved. Widths
 * are set from the headers, which are the widest thing in most columns and never
 * wrap.
 *
 * Each is its header (12px medium, plus the 24px of horizontal padding) or its
 * widest cell, whichever is larger, and nothing past that: the total has to
 * clear the ~1452px a full-width card leaves, or the table scrolls sideways
 * even with the window as wide as the shell allows. The two long First-year
 * headers are what holds the floor up now — they can't come in further without
 * being allowed to wrap.
 */
const BASE_COLUMNS: { key: string; width: number }[] = [
  { key: "month", width: 96 },
  { key: "budget", width: 80 },
  // Carries the "As of ..." detail line under the figure.
  { key: "adSpend", width: 108 },
  { key: "utilization", width: 100 },
  { key: "impressions", width: 100 },
  { key: "clicks", width: 76 },
  { key: "conversions", width: 104 },
  { key: "immediateAdSales", width: 132 },
  { key: "immediateRoas", width: 128 },
];

const AD_CLICK_COLUMNS = [
  ...BASE_COLUMNS,
  { key: "continuedAdSales", width: 132 },
  // Extra width for the lift tag so it isn't flush against First-year sales.
  { key: "totalSales", width: 136 },
  { key: "firstYearAdSales", width: 140 },
  { key: "firstYearRoas", width: 128 },
];

const ORDER_MONTH_COLUMNS = [
  ...BASE_COLUMNS,
  { key: "continuedAdSales", width: 132 },
  // Extra width for the lift tag so it isn't flush against Total sales / spend.
  { key: "totalSales", width: 136 },
  { key: "totalSalesOverSpend", width: 148 },
];

/**
 * V2's optional section, appended to the right of the order-month columns
 * rather than replacing them: the reorders a month's own customers went on to
 * place, on the cohort basis the row's drilldown uses. Same two labels as V1's
 * ad-click Continued sales and Total sales — the group band is what tells
 * those headers apart from the order-month pair three columns to the left.
 */
const COHORT_SECTION_COLUMNS = [
  { key: "cohortContinuedSales", width: 132 },
  { key: "cohortTotalSales", width: 136 },
  { key: "firstYearAdSales", width: 140 },
  { key: "firstYearRoas", width: 128 },
];

const V3_DEFAULT_COLUMN_KEYS = new Set([
  "month",
  "budget",
  "adSpend",
  "utilization",
  "conversions",
  "immediateAdSales",
  "immediateRoas",
  "totalSales",
  "totalSalesOverSpend",
]);

/**
 * V1's three independently hideable columns. Impressions and Clicks are
 * traffic no other column is derived from, and Continued sales is an addend of
 * Total sales rather than a figure the row needs to stay readable.
 */
const V1_HIDEABLE_COLUMNS = [
  { key: "impressions", label: "Impressions" },
  { key: "clicks", label: "Clicks" },
  { key: "continuedAdSales", label: "Continued sales" },
];

/**
 * V2 treats the cohort-oriented columns as one section, off by default. The
 * setting names the customer behavior it reveals rather than exposing four
 * implementation-level column names as four independent switches. Reorders from
 * previous months' customers are not in here: that section is the order-month
 * default and is always shown.
 */
const COHORT_SECTION_KEY = "cohortPerformance";
const V2_HIDEABLE_COLUMNS = [
  { key: "impressions", label: "Impressions" },
  { key: "clicks", label: "Clicks" },
  {
    key: COHORT_SECTION_KEY,
    label: "Reorders from each month's ad-acquired customers",
  },
];

const HEADER_TOOLTIPS = COPY.overview.monthTable;

/** Shared by Utilization and % of budget, which are both a share of budget. */
function formatBudgetPercent(value: number | null) {
  if (value == null) {
    return "—";
  }
  // One decimal, but drop a trailing ".0" (e.g. "88%" not "88.0%") to match the platform.
  const pct = Math.round(value * 1000) / 10;
  return `${Number.isInteger(pct) ? pct : pct.toFixed(1)}%`;
}

function BudgetPercentCell({ value }: { value: number | null }) {
  if (value == null) {
    return <EmptyCell />;
  }
  return (
    <span className="text-sm leading-5 text-[#333]">
      {formatBudgetPercent(value)}
    </span>
  );
}

function DeltaTag({ pct }: { pct: number }) {
  return (
    <span className="inline-flex items-center rounded bg-[#eef1ff] px-1.5 py-0.5 text-xs leading-4 font-medium text-[#3b3f8f]">
      +{pct}%
    </span>
  );
}

/**
 * Explains first-year reorders, not the First-year sales (actual) figure
 * beside it — that's the cell's job. The count and the sentence come from the
 * same places the drilldown's tag reads them from, so clicking the row can't
 * produce a different number for the same month.
 *
 * The tag goes bare when the cohort has no additional-order count to name: the
 * sentence is built around that count, and a percentage no reader can unpack is
 * a smaller cost than one claiming zero orders produced it.
 */
function ContinuedLiftTag({
  pct,
  monthIndex,
  variant = "cohort",
}: {
  pct: number;
  monthIndex: number;
  variant?: "cohort" | "order-month";
}) {
  if (variant === "order-month") {
    return (
      <HeaderTooltip
        tooltip={renderCopy(
          HEADER_TOOLTIPS.orderMonthLiftTag,
          {
            month: monthLabelFromIndex(monthIndex),
            pct,
          },
          "font-semibold"
        )}
      >
        <DeltaTag pct={pct} />
      </HeaderTooltip>
    );
  }

  const additionalOrders = getCohortAdditionalOrders(monthIndex);

  if (additionalOrders == null || additionalOrders <= 0) {
    return <DeltaTag pct={pct} />;
  }

  return (
    <HeaderTooltip
      tooltip={renderCopy(
        HEADER_TOOLTIPS.liftTag,
        {
          count: formatOverviewCount(additionalOrders),
          orders: additionalOrders === 1 ? "order" : "orders",
          // Short form, matching the drilldown this row links to.
          month: monthLabelFromIndex(monthIndex),
          pct,
        },
        "font-semibold"
      )}
    >
      <DeltaTag pct={pct} />
    </HeaderTooltip>
  );
}

export function AdPerformanceByMonthTable({
  version,
  organizeBy,
  rows,
  cohortRows,
  onOpenMonth,
}: AdPerformanceByMonthTableProps) {
  const isOrderMonth = organizeBy === "order-month";
  const isV2 = version >= 2;
  const isV3 = version === 3;
  const isCompact = useIsCompact();
  const cardPaddingClass = useCardPaddingClass();
  const rowHeightClass = useTableRowHeightClass();
  const [page, setPage] = useState(1);
  const [showAllColumns, setShowAllColumns] = useState(false);
  // Scoped to this table rather than the tab. It only ever re-cut these rows,
  // and sitting up with the date range implied it also moved the headline
  // metrics and the charts, which it never did.
  const [segment, setSegment] =
    useState<InsightsCustomerSegment>("all-customers");
  // Kept across a basis switch: hiding Clicks is a reading preference, not a
  // property of either accounting basis.
  const [hiddenColumns, setHiddenColumns] = useState<string[]>(() =>
    isV2 ? [COHORT_SECTION_KEY] : []
  );
  useEffect(() => {
    setHiddenColumns(isV2 ? [COHORT_SECTION_KEY] : []);
  }, [isV2]);
  useEffect(() => {
    setShowAllColumns(false);
  }, [isV3]);
  // Mounted at the page root, since its other trigger is a link inside a
  // tooltip panel that unmounts under the pointer.
  const openAdTypesModal = useAdTypesModal();
  // Drives the frozen column's shadow. Re-setting it to the same value on every
  // scroll event is free — React bails out of the render when the state is
  // unchanged — so this needs no ref or throttle.
  const [isScrolled, setIsScrolled] = useState(false);
  // Closed months read as links into the drilldown; the in-progress month is left
  // plain because its cohort is still forming. Keyed off the month index rather
  // than the "Active" detail line so it doesn't hang on a display string.
  const currentMonthIndex = getCurrentMonthIndex();
  const segmentRows = isOrderMonth
    ? applySegmentToOrderMonthRows(rows, segment)
    : applySegmentToActivityRows(rows, segment);
  const cohortRowsByMonth = new Map(
    (cohortRows
      ? applySegmentToActivityRows(cohortRows, segment)
      : segmentRows
    ).map((row) => [row.monthIndex, row])
  );
  const totalPages = Math.max(1, Math.ceil(segmentRows.length / PAGE_SIZE));
  // Clamped rather than reset: the segment filter re-cuts the same months, so
  // changing it should leave you on the page you were reading.
  const currentPage = Math.min(page, totalPages);
  const pageRows = segmentRows.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );
  const pageRowsWithCohort = pageRows.map((row) => ({
    row,
    cohortRow: cohortRowsByMonth.get(row.monthIndex) ?? row,
  }));
  // Utilization forecasts the whole month's budget against all ad types, so it
  // can't be cut to one. Filtered, the column reports that ad type's spend as a
  // share of the shared budget instead — a different measure, hence a different
  // header. Reorders land in the month they happen, so a reorder-ad view has
  // no first-year reorders to lift the actuals, and no first year to project a
  // total or a return into.
  const showsUtilization = segment === "all-customers";
  // First-year sales (actual) reports on every filter: under reorder ads it
  // equals Ad sales. The lift tag is what doesn't apply there — nothing
  // continues past the click month — so the tag drops and the figure stays.
  const continuedApplies = segment !== "returning-customers";
  const showsUtilizationColumn = true;
  const showsImpressions = isV3
    ? showAllColumns
    : !hiddenColumns.includes("impressions");
  const showsClicks = isV3 ? showAllColumns : !hiddenColumns.includes("clicks");
  const showsImmediateRoas = true;
  const showsContinued = isV3
    ? showAllColumns
    : !hiddenColumns.includes("continuedAdSales");
  const showsLiftTag = !isV3;
  // V2 only: the cohort section rides alongside the order-month one, which
  // stays put. Turning it off leaves the same table V2 opens with.
  const showsCohortSection =
    !isV3 && isV2 && !hiddenColumns.includes(COHORT_SECTION_KEY);
  // The click-month reading of the third section — continued and total sales
  // followed by first-year sales and ROAS. Order month (V1's toggle and every
  // V2 view) reports that month's own reorders and a total over spend instead.
  const usesFirstYearColumns = !isOrderMonth;
  const sourceColumns = isV3
    ? showAllColumns
      ? ORDER_MONTH_COLUMNS
      : ORDER_MONTH_COLUMNS.filter((column) =>
          V3_DEFAULT_COLUMN_KEYS.has(column.key)
        )
    : isOrderMonth
      ? showsCohortSection
        ? [...ORDER_MONTH_COLUMNS, ...COHORT_SECTION_COLUMNS]
        : ORDER_MONTH_COLUMNS
      : AD_CLICK_COLUMNS;
  const columns = sourceColumns.filter(
    (column) => !hiddenColumns.includes(column.key)
  );
  const tableMinWidth = columns.reduce(
    (total, column) => total + column.width,
    0
  );
  // The group bands span whatever is left of each section, so a hidden column
  // has to come off its band's colSpan or the three labels drift out of line
  // with the columns they describe.
  const spendGroupSpan = 3 + (showsImpressions ? 1 : 0) + (showsClicks ? 1 : 0);
  const immediateGroupSpan = 2 + (showsImmediateRoas ? 1 : 0);
  const continuedGroupSpan =
    (usesFirstYearColumns ? 4 : 3) - (showsContinued ? 0 : 1);
  // The divider belongs to the section, not to Continued sales, so it moves to
  // Total sales when Continued sales is switched off.
  const continuedEdgeClass = showsContinued ? SECTION_EDGE_CLASS : "";
  const totalSalesEdgeClass = showsContinued ? "" : SECTION_EDGE_CLASS;
  const stickyMonthClass = isScrolled
    ? `${STICKY_MONTH_CLASS} ${STICKY_SCROLLED_CLASS}`
    : STICKY_MONTH_CLASS;
  const sectionEdgeClass = SECTION_EDGE_CLASS;
  const monthSectionEdgeClass = MONTH_SECTION_EDGE_CLASS;
  // The column-header row loses its gray fill on V3, matching the conversions
  // tables. Month stays opaque because it's sticky and cells scroll under it.
  const headerRowClass = isV3 ? "" : "bg-[#f7f7f7]";
  const headerMonthFillClass = isV3 ? "bg-fs-surface-primary" : "bg-[#f7f7f7]";

  return (
    <section
      className={`bg-fs-surface-primary rounded-lg border border-[#dfe0e1] ${cardPaddingClass}`}
    >
      {/* Title and controls share the card's top edge. V2 hangs the
          description off the title as a tooltip so the table can start
          sooner; V1 keeps it as a paragraph under the heading. V3 stacks
          the filters under the title so they read as belonging to the
          table rather than sitting opposite it. */}
      <div
        className={
          isV3
            ? "flex flex-col items-start gap-4"
            : "flex items-start justify-between gap-6"
        }
      >
        <div>
          <h3 className="text-sm leading-5 font-medium text-[#333]">
            {isV2 ? (
              <HeaderTooltip
                interactive={!isV3}
                tooltip={
                  isV3
                    ? HEADER_TOOLTIPS.v3Description
                    : renderCopy(HEADER_TOOLTIPS.v2Description, {
                        adTypes: openAdTypesModal ? (
                          <button
                            type="button"
                            className="text-[#333] underline"
                            onClick={openAdTypesModal}
                          >
                            {HEADER_TOOLTIPS.adTypesLink}
                          </button>
                        ) : (
                          HEADER_TOOLTIPS.adTypesLink
                        ),
                      })
                }
              >
                <DottedLabel>{HEADER_TOOLTIPS.v2Title}</DottedLabel>
              </HeaderTooltip>
            ) : isOrderMonth ? (
              HEADER_TOOLTIPS.orderMonthTitle
            ) : (
              HEADER_TOOLTIPS.adClickMonthTitle
            )}
          </h3>
          {!isV2 ? (
            <p className="type-fs-paragraph mt-1 max-w-[1000px] text-[#757575]">
              {renderCopy(
                isOrderMonth
                  ? HEADER_TOOLTIPS.orderMonthDescription
                  : HEADER_TOOLTIPS.adClickMonthDescription,
                {
                  adTypes: openAdTypesModal ? (
                    <button
                      type="button"
                      className="text-[#333] underline"
                      onClick={openAdTypesModal}
                    >
                      {HEADER_TOOLTIPS.adTypesLink}
                    </button>
                  ) : (
                    HEADER_TOOLTIPS.adTypesLink
                  ),
                }
              )}
            </p>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <InsightsFilterMenu
            name="month-table-ad-type"
            labelPrefix="Ad type"
            menuTitle="Filter by ad type"
            value={segment}
            options={AD_TYPE_OPTIONS}
            onChange={setSegment}
            clearValue="all-customers"
            titleInfo={
              openAdTypesModal
                ? {
                    label: COPY.overview.adTypesModal.title,
                    onClick: openAdTypesModal,
                  }
                : undefined
            }
          />
          {isV3 ? (
            <InsightsFilterMenu
              name="month-table-columns"
              labelPrefix="Columns"
              menuTitle="Table columns"
              value={showAllColumns ? "all" : "essential"}
              options={TABLE_COLUMNS_OPTIONS}
              onChange={(value) => setShowAllColumns(value === "all")}
              clearValue="essential"
            />
          ) : (
            <TableSettingsMenu
              columns={isV2 ? V2_HIDEABLE_COLUMNS : V1_HIDEABLE_COLUMNS}
              hidden={hiddenColumns}
              onToggle={(key) =>
                setHiddenColumns((current) =>
                  current.includes(key)
                    ? current.filter((hidden) => hidden !== key)
                    : [...current, key]
                )
              }
              onShowAll={() => setHiddenColumns([])}
            />
          )}
        </div>
      </div>
      {/* `isolate` keeps the frozen column's z-10 inside this container. Slate
          portals the Ad type popup to the body with no z-index of its own, and a
          positioned element with a raised z-index paints above one at `auto`
          whatever the DOM order — so without this the Month column covers the
          menu above it. Contained here, the frozen column still outranks the
          cells scrolling under it and nothing else. */}
      <div
        className={`isolate overflow-x-auto ${isCompact ? "mt-4" : "mt-6"}`}
        onScroll={(event) => setIsScrolled(event.currentTarget.scrollLeft > 0)}
      >
        {/* 8px on each end of every row, header included, so the first and last
            columns aren't flush against the card. V3 uses 16px — still a hairline
            inset, just enough that the figures aren't sitting on the card edge.
            Applied by position rather than to specific cells because the trailing
            cell is the zero-width row action.

            V3 also right-aligns every column after Month, so figures line up on
            their last digit and a column reads as one number rather than as
            ragged text. The first header row is left out of it: those are the
            colored group bands, which label a span of columns and belong at the
            start of the span they cover. */}
        <table
          className={`w-full table-fixed text-left text-sm ${
            isV3
              ? "[&_tbody_tr>*:not(:first-child)]:text-right [&_thead_tr:last-child>*:not(:first-child)]:text-right [&_tr>*:first-child]:pl-4 [&_tr>*:last-child]:pr-4"
              : "[&_tr>*:first-child]:pl-2 [&_tr>*:last-child]:pr-2"
          }`}
          style={{ minWidth: `${tableMinWidth}px` }}
        >
          <colgroup>
            {columns.map((column) => (
              <col key={column.key} style={{ width: `${column.width}px` }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th
                className={`${stickyMonthClass} ${monthSectionEdgeClass} bg-fs-surface-primary border-b border-[#dfe0e1]`}
                aria-hidden
              />
              <th
                colSpan={spendGroupSpan}
                className={`${GROUP_IMMEDIATE_CLASS} ${sectionEdgeClass}`}
              >
                <GroupLabel color={GROUP_COLORS.spend}>
                  {HEADER_TOOLTIPS.groupSpend}
                </GroupLabel>
              </th>
              <th
                colSpan={immediateGroupSpan}
                className={`${GROUP_IMMEDIATE_CLASS} ${sectionEdgeClass}`}
              >
                <GroupLabel color={GROUP_COLORS.immediate}>
                  {HEADER_TOOLTIPS.groupClickOrders}
                </GroupLabel>
              </th>
              <th
                colSpan={continuedGroupSpan}
                className={`${GROUP_FIRST_YEAR_CLASS} ${sectionEdgeClass}`}
              >
                <GroupLabel color={GROUP_COLORS.continued}>
                  {isV3
                    ? HEADER_TOOLTIPS.groupAllAdAttributedOrders
                    : usesFirstYearColumns
                      ? HEADER_TOOLTIPS.groupClickContinued
                      : HEADER_TOOLTIPS.groupOrderContinued}
                </GroupLabel>
              </th>
              {showsCohortSection ? (
                <th
                  colSpan={COHORT_SECTION_COLUMNS.length}
                  className={`${GROUP_FIRST_YEAR_CLASS} ${sectionEdgeClass}`}
                >
                  <GroupLabel color={GROUP_COLORS.continued}>
                    {HEADER_TOOLTIPS.groupClickContinued}
                  </GroupLabel>
                </th>
              ) : null}
            </tr>
            <tr className={`border-b border-[#dfe0e1] ${headerRowClass}`}>
              <th
                className={`faire-label-medium ${stickyMonthClass} ${monthSectionEdgeClass} border-b border-[#dfe0e1] ${headerMonthFillClass} py-3 pr-3 whitespace-nowrap text-[#333]`}
              >
                Month
              </th>
              <th
                className={`faire-label-medium px-3 py-3 whitespace-nowrap text-[#333] ${sectionEdgeClass}`}
              >
                Budget
              </th>
              <th className="faire-label-medium px-3 py-3 whitespace-nowrap text-[#333]">
                <HeaderTooltip tooltip={HEADER_TOOLTIPS.adSpend}>
                  <DottedLabel>Ad spend</DottedLabel>
                </HeaderTooltip>
              </th>
              {showsUtilizationColumn ? (
                <th className="faire-label-medium px-3 py-3 whitespace-nowrap text-[#333]">
                  {/* Picked off `segment` rather than a shared tooltip key,
                      which would carry an `all` case this column can't have: under all
                      ad types the header is Utilization instead. */}
                  <HeaderTooltip
                    tooltip={
                      showsUtilization
                        ? HEADER_TOOLTIPS.utilization
                        : HEADER_TOOLTIPS.budgetShare[
                            segment === "new-customers" ? "new" : "returning"
                          ]
                    }
                  >
                    <DottedLabel>
                      {showsUtilization ? "Utilization" : "% of budget"}
                    </DottedLabel>
                  </HeaderTooltip>
                </th>
              ) : null}
              {showsImpressions ? (
                <th className="faire-label-medium px-3 py-3 whitespace-nowrap text-[#333]">
                  Impressions
                </th>
              ) : null}
              {showsClicks ? (
                <th className="faire-label-medium px-3 py-3 whitespace-nowrap text-[#333]">
                  Clicks
                </th>
              ) : null}
              <th
                className={`faire-label-medium px-3 py-3 whitespace-nowrap text-[#333] ${sectionEdgeClass}`}
              >
                <HeaderTooltip tooltip={HEADER_TOOLTIPS.conversions}>
                  <DottedLabel>Conversions</DottedLabel>
                </HeaderTooltip>
              </th>
              <th className="faire-label-medium px-3 py-3 whitespace-nowrap text-[#333]">
                <HeaderTooltip
                  tooltip={
                    isV3
                      ? HEADER_TOOLTIPS.v3ImmediateAdSales
                      : HEADER_TOOLTIPS.immediateAdSales
                  }
                >
                  <DottedLabel>Immediate sales</DottedLabel>
                </HeaderTooltip>
              </th>
              {showsImmediateRoas ? (
                <th className="faire-label-medium px-3 py-3 whitespace-nowrap text-[#333]">
                  <HeaderTooltip
                    tooltip={
                      isV3
                        ? HEADER_TOOLTIPS.v3FirstOrderRoas
                        : HEADER_TOOLTIPS.firstOrderRoas
                    }
                  >
                    <DottedLabel>Immediate ROAS</DottedLabel>
                  </HeaderTooltip>
                </th>
              ) : null}
              {showsContinued ? (
                <th
                  className={`faire-label-medium px-3 py-3 whitespace-nowrap text-[#333] ${continuedEdgeClass}`}
                >
                  <HeaderTooltip
                    tooltip={
                      continuedApplies
                        ? usesFirstYearColumns
                          ? HEADER_TOOLTIPS.continuedSales
                          : isV3
                            ? HEADER_TOOLTIPS.v3ContinuedSales
                            : HEADER_TOOLTIPS.orderMonthContinuedSales
                        : HEADER_TOOLTIPS.firstYearAdSalesReorder
                    }
                  >
                    <DottedLabel>Continued sales</DottedLabel>
                  </HeaderTooltip>
                </th>
              ) : null}
              <th
                className={`faire-label-medium py-3 pr-4 pl-3 whitespace-nowrap text-[#333] ${totalSalesEdgeClass}`}
              >
                <HeaderTooltip
                  tooltip={
                    isV3
                      ? HEADER_TOOLTIPS.v3TotalSales
                      : HEADER_TOOLTIPS.totalSales
                  }
                >
                  <DottedLabel>
                    {isV3 ? "Total ad sales" : "Total sales"}
                  </DottedLabel>
                </HeaderTooltip>
              </th>
              {usesFirstYearColumns ? (
                <>
                  <th className="faire-label-medium px-3 py-3 whitespace-nowrap text-[#333]">
                    <HeaderTooltip tooltip={HEADER_TOOLTIPS.firstYearAdSales}>
                      <DottedLabel>First-year sales</DottedLabel>
                    </HeaderTooltip>
                  </th>
                  <th className="faire-label-medium px-3 py-3 whitespace-nowrap text-[#333]">
                    <HeaderTooltip
                      interactive={continuedApplies}
                      tooltip={
                        continuedApplies
                          ? forecastTooltip(HEADER_TOOLTIPS.firstYearRoas)
                          : HEADER_TOOLTIPS.firstYearRoasReorder
                      }
                    >
                      <DottedLabel>First-year ROAS</DottedLabel>
                    </HeaderTooltip>
                  </th>
                </>
              ) : (
                <th className="faire-label-medium px-3 py-3 whitespace-nowrap text-[#333]">
                  <HeaderTooltip
                    tooltip={
                      isV3
                        ? HEADER_TOOLTIPS.v3TotalSalesOverSpend
                        : HEADER_TOOLTIPS.totalSalesOverSpend
                    }
                  >
                    <DottedLabel>
                      {isV3 ? "Total ad sales / spend" : "Total sales / spend"}
                    </DottedLabel>
                  </HeaderTooltip>
                </th>
              )}
              {showsCohortSection ? (
                <>
                  <th
                    className={`faire-label-medium px-3 py-3 whitespace-nowrap text-[#333] ${sectionEdgeClass}`}
                  >
                    <HeaderTooltip
                      tooltip={
                        continuedApplies
                          ? HEADER_TOOLTIPS.continuedSales
                          : HEADER_TOOLTIPS.firstYearAdSalesReorder
                      }
                    >
                      <DottedLabel>Continued sales</DottedLabel>
                    </HeaderTooltip>
                  </th>
                  <th className="faire-label-medium py-3 pr-4 pl-3 whitespace-nowrap text-[#333]">
                    <HeaderTooltip tooltip={HEADER_TOOLTIPS.totalSales}>
                      <DottedLabel>
                        {isV3 ? "Total ad sales" : "Total sales"}
                      </DottedLabel>
                    </HeaderTooltip>
                  </th>
                  <th className="faire-label-medium px-3 py-3 whitespace-nowrap text-[#333]">
                    <HeaderTooltip tooltip={HEADER_TOOLTIPS.firstYearAdSales}>
                      <DottedLabel>First-year sales</DottedLabel>
                    </HeaderTooltip>
                  </th>
                  <th className="faire-label-medium px-3 py-3 whitespace-nowrap text-[#333]">
                    <HeaderTooltip
                      interactive={continuedApplies}
                      tooltip={
                        continuedApplies
                          ? forecastTooltip(HEADER_TOOLTIPS.firstYearRoas)
                          : HEADER_TOOLTIPS.firstYearRoasReorder
                      }
                    >
                      <DottedLabel>First-year ROAS</DottedLabel>
                    </HeaderTooltip>
                  </th>
                </>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {pageRowsWithCohort.map(({ row, cohortRow }) => (
              <tr
                key={row.key}
                className={`group border-b border-[#dfe0e1] last:border-0 ${rowHeightClass} ${
                  onOpenMonth ? "cursor-pointer hover:bg-[#fafafa]" : ""
                } ${
                  // The in-progress month carries two detail lines the closed
                  // months don't ("Active", "As of ..."), which on V3's shorter
                  // rows made it the one row standing taller than the rest.
                  // Tightening its cell padding absorbs the second line instead
                  // of dropping it.
                  isV3 && row.monthIndex === currentMonthIndex
                    ? "[&>*]:py-1!"
                    : ""
                }`}
                onClick={
                  onOpenMonth ? () => onOpenMonth(row.monthIndex) : undefined
                }
              >
                <td
                  className={`${stickyMonthClass} ${monthSectionEdgeClass} bg-fs-surface-primary border-b border-[#dfe0e1] py-3 pr-3 whitespace-nowrap text-[#333] group-last:border-b-0 ${
                    onOpenMonth ? "group-hover:bg-[#fafafa]" : ""
                  }`}
                >
                  <div
                    className={
                      !onOpenMonth || row.monthIndex === currentMonthIndex
                        ? MONTH_LABEL_CLASS
                        : `${MONTH_LABEL_CLASS} underline`
                    }
                  >
                    {row.periodLabel}
                  </div>
                  {row.periodDetail ? (
                    <div className="type-fs-label-medium text-[#49694c]">
                      {row.periodDetail}
                    </div>
                  ) : null}
                </td>
                <td className={`px-3 py-3 text-[#333] ${sectionEdgeClass}`}>
                  <div className="text-sm leading-5 underline">
                    {row.budget}
                  </div>
                </td>
                <td className="px-3 py-3 text-[#333]">
                  <div className="text-sm leading-5">{row.adSpend}</div>
                  {row.adSpendDetail ? (
                    <div className="text-xs leading-4 whitespace-nowrap text-[#757575]">
                      {row.adSpendDetail}
                    </div>
                  ) : null}
                </td>
                {showsUtilizationColumn ? (
                  <td className="px-3 py-3">
                    {/* Only utilization gets the projected tag. A share of budget
                        is spend that has already gone out, so it's a real figure
                        even in a month that's still running. */}
                    {!showsUtilization ? (
                      <BudgetPercentCell value={getRowBudgetShare(row)} />
                    ) : row.utilization == null ? (
                      <EmptyCell />
                    ) : row.utilizationAsTag ? (
                      <HeaderTooltip
                        tooltip={HEADER_TOOLTIPS.utilizationProjected}
                      >
                        <ProjectedTag>
                          {formatBudgetPercent(row.utilization)}
                        </ProjectedTag>
                      </HeaderTooltip>
                    ) : (
                      <BudgetPercentCell value={row.utilization} />
                    )}
                  </td>
                ) : null}
                {showsImpressions ? (
                  <td className="px-3 py-3 text-sm leading-5 text-[#333]">
                    {row.impressions}
                  </td>
                ) : null}
                {showsClicks ? (
                  <td className="px-3 py-3 text-sm leading-5 text-[#333]">
                    {row.clicks}
                  </td>
                ) : null}
                <td
                  className={`px-3 py-3 text-sm leading-5 text-[#333] ${sectionEdgeClass} ${
                    onOpenMonth ? "underline" : ""
                  }`}
                >
                  {row.conversions}
                </td>
                <td className="px-3 py-3 text-sm leading-5 text-[#333]">
                  {row.firstOrderSales}
                </td>
                {showsImmediateRoas ? (
                  <td className="px-3 py-3 text-sm leading-5 text-[#333]">
                    {row.firstOrderRoas}
                  </td>
                ) : null}
                {showsContinued ? (
                  <td
                    className={`px-3 py-3 text-sm leading-5 text-[#333] ${continuedEdgeClass}`}
                  >
                    {formatOverviewCurrency(
                      Math.max(0, row.salesToDateDeltaDollars ?? 0)
                    )}
                  </td>
                ) : null}
                <td
                  className={`py-3 pr-4 pl-3 text-sm leading-5 text-[#333] ${totalSalesEdgeClass}`}
                >
                  <span className="inline-flex items-center gap-2">
                    {row.salesToDate}
                    {showsLiftTag &&
                    continuedApplies &&
                    row.salesToDateDeltaPct != null &&
                    row.salesToDateDeltaPct > 0 ? (
                      <ContinuedLiftTag
                        pct={row.salesToDateDeltaPct}
                        monthIndex={row.monthIndex}
                        variant={isOrderMonth ? "order-month" : "cohort"}
                      />
                    ) : null}
                  </span>
                </td>
                {usesFirstYearColumns ? (
                  <>
                    <td className="px-3 py-3">
                      {row.firstYearRoasProjected ? (
                        <HeaderTooltip
                          interactive={continuedApplies}
                          tooltip={
                            continuedApplies
                              ? forecastTooltip(
                                  HEADER_TOOLTIPS.firstYearAdSalesProjected
                                )
                              : HEADER_TOOLTIPS.firstYearAdSalesReorder
                          }
                        >
                          <ProjectedTag>
                            {formatOverviewCurrency(row.firstYearSalesAmount)}
                          </ProjectedTag>
                        </HeaderTooltip>
                      ) : (
                        <span className="text-sm leading-5 text-[#333]">
                          {formatOverviewCurrency(row.firstYearSalesAmount)}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      {row.firstYearRoasProjected ? (
                        <HeaderTooltip
                          interactive={continuedApplies}
                          tooltip={
                            continuedApplies
                              ? forecastTooltip(
                                  HEADER_TOOLTIPS.firstYearRoasProjected
                                )
                              : HEADER_TOOLTIPS.firstYearRoasReorder
                          }
                        >
                          <ProjectedTag>{row.firstYearRoas}</ProjectedTag>
                        </HeaderTooltip>
                      ) : (
                        <span className="text-sm leading-5 text-[#333]">
                          {row.firstYearRoas}
                        </span>
                      )}
                    </td>
                  </>
                ) : (
                  <td className="px-3 py-3">
                    <span className="text-sm leading-5 text-[#333]">
                      {row.firstYearRoas}
                    </span>
                  </td>
                )}
                {showsCohortSection ? (
                  <>
                    <td
                      className={`px-3 py-3 text-sm leading-5 text-[#333] ${sectionEdgeClass}`}
                    >
                      {formatOverviewCurrency(
                        Math.max(0, cohortRow.salesToDateDeltaDollars ?? 0)
                      )}
                    </td>
                    <td className="py-3 pr-4 pl-3 text-sm leading-5 text-[#333]">
                      <span className="inline-flex items-center gap-2">
                        {cohortRow.salesToDate}
                        {!isV3 &&
                        continuedApplies &&
                        cohortRow.salesToDateDeltaPct != null &&
                        cohortRow.salesToDateDeltaPct > 0 ? (
                          <ContinuedLiftTag
                            pct={cohortRow.salesToDateDeltaPct}
                            monthIndex={row.monthIndex}
                          />
                        ) : null}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      {cohortRow.firstYearRoasProjected ? (
                        <HeaderTooltip
                          interactive={continuedApplies}
                          tooltip={
                            continuedApplies
                              ? forecastTooltip(
                                  HEADER_TOOLTIPS.firstYearAdSalesProjected
                                )
                              : HEADER_TOOLTIPS.firstYearAdSalesReorder
                          }
                        >
                          <ProjectedTag>
                            {formatOverviewCurrency(
                              cohortRow.firstYearSalesAmount
                            )}
                          </ProjectedTag>
                        </HeaderTooltip>
                      ) : (
                        <span className="text-sm leading-5 text-[#333]">
                          {formatOverviewCurrency(
                            cohortRow.firstYearSalesAmount
                          )}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      {cohortRow.firstYearRoasProjected ? (
                        <HeaderTooltip
                          interactive={continuedApplies}
                          tooltip={
                            continuedApplies
                              ? forecastTooltip(
                                  HEADER_TOOLTIPS.firstYearRoasProjected
                                )
                              : HEADER_TOOLTIPS.firstYearRoasReorder
                          }
                        >
                          <ProjectedTag>{cohortRow.firstYearRoas}</ProjectedTag>
                        </HeaderTooltip>
                      ) : (
                        <span className="text-sm leading-5 text-[#333]">
                          {cohortRow.firstYearRoas}
                        </span>
                      )}
                    </td>
                  </>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 ? (
        <div className="mt-4 flex justify-center">
          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            onClick={setPage}
          />
        </div>
      ) : null}
    </section>
  );
}
